import { Controller } from 'react-hook-form';
import { Field } from '../../../forms/fields';
import type { SectionProps } from '../sectionProps';
import { type AnyFieldError, toFieldError } from './fieldError';

/**
 * Shared TIMEPOINT field (PLAN.md M7 — used by income/expense start·end and event `at`). A
 * `TimePoint` is the engine's discriminated union: an ABSOLUTE calendar `{kind:'year', year}` OR an
 * EVENT-RELATIVE anchor `{kind:'event', eventId, offsetYears}`. This control renders a `kind` toggle
 * and the matching inputs:
 *  - `year` → a numeric year input;
 *  - `event` → a select over the plan's declared events (the anchor) + a signed `offsetYears`
 *    (negative = before the event, e.g. "3 years before retirement").
 *
 * Validation (a missing/unknown `eventId`) comes from `PlanSchema`'s superRefine, surfaced on the
 * owning section's `events` error path — so the inline error rendered here is the engine's own.
 *
 * A `Controller` drives the whole TimePoint object so switching `kind` swaps the value SHAPE
 * cleanly (rather than leaving stale `year`/`eventId` keys that the `.strict()` schema rejects).
 */

export interface TimePointFieldProps {
  /** The shared form handle (to read the plan's events for the anchor select). */
  form: SectionProps['form'];
  /** The dot-path to the TimePoint on the plan, e.g. `incomes.0.start`. */
  name:
    | `incomes.${number}.start`
    | `incomes.${number}.end`
    | `expenses.${number}.start`
    | `expenses.${number}.end`
    | `events.${number}.at`;
  /** Field label, e.g. "Start" / "End" / "When". */
  label: string;
  /** When true, an "absent" (undefined) choice is offered — for an optional `end`. */
  optional?: boolean | undefined;
  /** Inline error (e.g. the superRefine "unknown eventId" message routed here). */
  error?: AnyFieldError;
}

type TimePointValue =
  | { kind: 'year'; year: number }
  | { kind: 'event'; eventId: string; offsetYears: number };

const DEFAULT_YEAR = new Date().getFullYear();

export function TimePointField({
  form,
  name,
  label,
  optional,
  error,
}: TimePointFieldProps): React.ReactElement {
  const events = form.watch('events');
  const eventOptions = events.map((e) => ({
    id: e.id,
    label: e.name.length > 0 ? `${e.name} (${e.id})` : e.id,
  }));

  return (
    <Controller
      control={form.control}
      name={name}
      render={({ field }) => {
        const raw = field.value as TimePointValue | undefined;
        // For an optional `end`, "" = undefined (runs to horizon).
        const mode: 'none' | 'year' | 'event' =
          raw === undefined ? 'none' : raw.kind === 'event' ? 'event' : 'year';

        const handleMode = (next: 'none' | 'year' | 'event'): void => {
          if (next === 'none') {
            field.onChange(undefined);
          } else if (next === 'year') {
            field.onChange({ kind: 'year', year: DEFAULT_YEAR });
          } else {
            field.onChange({
              kind: 'event',
              eventId: eventOptions[0]?.id ?? '',
              offsetYears: 0,
            });
          }
        };

        return (
          <Field label={label} error={toFieldError(error)} htmlFor={`${name}.kind`}>
            <div className="timepoint">
              <select
                id={`${name}.kind`}
                className="select timepoint__kind"
                value={mode}
                onChange={(e) => handleMode(e.currentTarget.value as 'none' | 'year' | 'event')}
                onBlur={field.onBlur}
              >
                {optional === true && <option value="none">To horizon</option>}
                <option value="year">Calendar year</option>
                <option value="event">Relative to event</option>
              </select>

              {mode === 'year' && raw?.kind === 'year' && (
                <input
                  className="input input--num timepoint__year"
                  type="number"
                  aria-label={`${label} year`}
                  value={Number.isNaN(raw.year) ? '' : raw.year}
                  onBlur={field.onBlur}
                  onChange={(e) =>
                    field.onChange({ kind: 'year', year: e.currentTarget.valueAsNumber })
                  }
                />
              )}

              {mode === 'event' && raw?.kind === 'event' && (
                <>
                  <select
                    className="select timepoint__event"
                    aria-label={`${label} event`}
                    value={raw.eventId}
                    onBlur={field.onBlur}
                    onChange={(e) => field.onChange({ ...raw, eventId: e.currentTarget.value })}
                  >
                    {eventOptions.length === 0 && <option value="">No events defined</option>}
                    {eventOptions.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <input
                    className="input input--num timepoint__offset"
                    type="number"
                    aria-label={`${label} offset years`}
                    title="Offset in years (negative = before the event)"
                    value={Number.isNaN(raw.offsetYears) ? '' : raw.offsetYears}
                    onBlur={field.onBlur}
                    onChange={(e) =>
                      field.onChange({
                        ...raw,
                        offsetYears: e.currentTarget.valueAsNumber,
                      })
                    }
                  />
                  <span className="timepoint__suffix">yr offset</span>
                </>
              )}
            </div>
          </Field>
        );
      }}
    />
  );
}
